import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { getCoreDomain, customHeaders, CORE_API_ENTRYPOINT } from '@probatix/common/services/services.helpers'
import { OrderConfirmationStatus, OrderPayload } from '@probatix/common/services/toolkit/order/orderService.model'

import { getCurrentLocale } from '@probatix/common/translations'

import { noPagination } from '@probatix/common/services/RTKServices/locations/index.helpers'
import LocalStorage from '@probatix/common/utils/localStorage'
import { EXPIRATION_TIME_JWT, JWT } from '@probatix/common/services/services.constants'
import { EXPIRY_OF_JWT_TOKEN_MINUTES } from '@probatix/common/config/app'

import { basePathOfProduct, basePathOfProductList } from './checkoutStepsService.helpers'

const returnHeader = () => ({
  Accept: 'application/json',
  'Accept-Language': getCurrentLocale().code.slice(0, 2),
  'Content-Type': 'application/json',
})

// Define a service using a base URL and expected endpoints
export const checkoutStepsApi = createApi({
  baseQuery: fetchBaseQuery({ baseUrl: getCoreDomain() }),
  endpoints: (builder) => ({
    getOrderStatusJWT: builder.query<OrderConfirmationStatus, any>({
      query: ({ id }) => ({
        headers: customHeaders(),
        method: 'GET',
        url: `${CORE_API_ENTRYPOINT}/api/v0/orders/${id}`,
      }),
    }),
    getProduct: builder.query<any, any>({
      query: ({ url, uuid }) => ({
        headers: returnHeader(),
        method: 'GET',
        url: url ? `${url}/v0/checkout/product/${uuid}` : `${basePathOfProduct}/${uuid}`,
      }),
    }),
    getProducts: builder.query<any, any>({
      query: ({ locationCode }) => ({
        headers: returnHeader(),
        method: 'GET',
        url: `${basePathOfProductList}?${noPagination}${locationCode ? `&locationCode=${locationCode}` : ''}`,
      }),
    }),
    postOrder: builder.mutation<any, OrderPayload>({
      query: ({ customer,
        location,
        patientBookingInformations,
        paymentMethod,
        product,
        quickOrderUuid,
        selectedBookingTime,
        url,
        voucher }) => ({
        body: {
          customer,
          location,
          patientBookingInformations,
          paymentMethod,
          product,
          quickOrderUuid,
          selectedBookingTime,
          voucher,
        },
        headers: customHeaders(),
        method: 'POST',
        url: `${url}/api/v0/checkout/order`,
      }),
      transformResponse: (responseData: any) => {
        if (responseData.token) {

          const expiry = Date.now() + 1000 * 60 * Number(EXPIRY_OF_JWT_TOKEN_MINUTES)
          LocalStorage.set(JWT, responseData.token)
          LocalStorage.set(EXPIRATION_TIME_JWT, expiry)
        }

        return responseData
      },
    }),
    postVoucher: builder.mutation<any, any>({
      query: ({ customer, location, patientBookingInformations, product, selectedBookingTime, url, voucher }) => ({
        body: {
          customer,
          location,
          patientBookingInformations,
          product,
          selectedBookingTime,
          voucher,
        },
        headers: returnHeader(),
        method: 'POST',
        url: `${url}/api/v0/checkout/checkVoucher`,
      }),
    }),
  }),
  reducerPath: 'checkoutStepsApi',
})

export const useGetProducts = checkoutStepsApi.endpoints.getProducts.useLazyQuery
export const useGetProduct = checkoutStepsApi.endpoints.getProduct.useLazyQuery
export const usePostOrder = checkoutStepsApi.endpoints.postOrder.useMutation
export const useGetOrderStatusJWT = checkoutStepsApi.endpoints.getOrderStatusJWT
export const usePostVoucher = checkoutStepsApi.endpoints.postVoucher.useMutation

